import { supabase } from '../lib/supabase'
import { agentTemplateService } from './agentTemplateService'

export interface Agent {
    id: string
    user_id: string
    name: string
    handle: string | null
    description: string | null
    function_description: string | null
    avatar_url: string | null
    banner_url: string | null
    status: 'draft' | 'active' | null
    version: string | null
    created_at: string
}

export const agentService = {
    /**
     * Check if a handle is available
     */
    async checkHandleAvailability(handle: string): Promise<boolean> {
        // Handles must be at least 3 chars
        if (handle.length < 3) return false

        const { data, error } = await supabase
            .from('agents')
            .select('id')
            .eq('handle', handle)
            .maybeSingle()

        if (error) throw error
        return data === null
    },

    /**
     * Generate handle suggestions based on name
     */
    generateHandleSuggestions(name: string, basedOnTaken?: string): string[] {
        const base = (basedOnTaken || name)
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .slice(0, 20)

        if (!base) return ['agent_01', 'my_agent', 'new_assistant']

        return [
            `${base}_ai`,
            `${base}_bot`,
            `${base}${Math.floor(Math.random() * 1000)}`,
            `ai_${base}`,
            `${base}_assistant`
        ]
    },

    /**
     * Upload avatar image to Supabase Storage
     */
    async uploadAvatar(userId: string, file: File): Promise<string> {
        console.log('uploadAvatar called with:', {
            userId,
            fileName: file?.name,
            fileSize: file?.size,
            fileType: file?.type
        })

        // Validate file object
        if (!file) {
            throw new Error('No file provided')
        }

        if (!file.name) {
            throw new Error('File name is missing')
        }

        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession()
        console.log('Current session:', session ? 'Authenticated' : 'Not authenticated', session?.user?.id)

        if (!session) {
            throw new Error('User not authenticated')
        }

        // Convert File to ArrayBuffer to avoid serialization issues
        const arrayBuffer = await file.arrayBuffer()

        // Use very simple filename - just timestamp
        const timestamp = Date.now()
        const fileName = `avatar-${timestamp}.jpg`

        console.log('Uploading to path:', fileName, 'Size:', arrayBuffer.byteLength)

        // Upload using ArrayBuffer instead of File
        const { data, error } = await supabase.storage
            .from('agent-avatars')
            .upload(fileName, arrayBuffer, {
                cacheControl: '3600',
                upsert: true,
                contentType: file.type || 'image/jpeg'
            })

        if (error) {
            console.error('Upload error details:', {
                message: error.message,
                name: error.name,
                fullError: error
            })
            throw error
        }

        console.log('Upload successful, data:', data)

        const { data: { publicUrl } } = supabase.storage
            .from('agent-avatars')
            .getPublicUrl(data.path)

        console.log('Public URL:', publicUrl)

        return publicUrl
    },

    /**
     * Upload banner image to Supabase Storage
     */
    async uploadBanner(userId: string, file: File): Promise<string> {
        console.log('uploadBanner called with:', {
            userId,
            fileName: file?.name,
            fileSize: file?.size,
            fileType: file?.type
        })

        // Validate file object
        if (!file) {
            throw new Error('No file provided')
        }

        if (!file.name) {
            throw new Error('File name is missing')
        }

        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession()
        console.log('Current session:', session ? 'Authenticated' : 'Not authenticated', session?.user?.id)

        if (!session) {
            throw new Error('User not authenticated')
        }

        // Convert File to ArrayBuffer to avoid serialization issues
        const arrayBuffer = await file.arrayBuffer()

        // Use very simple filename - just timestamp
        const timestamp = Date.now()
        const fileName = `banner-${timestamp}.jpg`

        console.log('Uploading to path:', fileName, 'Size:', arrayBuffer.byteLength)

        // Upload using ArrayBuffer instead of File
        const { data, error } = await supabase.storage
            .from('agent-banners')
            .upload(fileName, arrayBuffer, {
                cacheControl: '3600',
                upsert: true,
                contentType: file.type || 'image/jpeg'
            })

        if (error) {
            console.error('Upload error details:', {
                message: error.message,
                name: error.name,
                fullError: error
            })
            throw error
        }

        console.log('Upload successful, data:', data)

        const { data: { publicUrl } } = supabase.storage
            .from('agent-banners')
            .getPublicUrl(data.path)

        console.log('Public URL:', publicUrl)

        return publicUrl
    },

    /**
     * Copy library image to user's storage
     */
    async copyLibraryImage(userId: string, libraryImageUrl: string): Promise<string> {
        console.log('copyLibraryImage called with:', {
            userId,
            libraryImageUrl
        })

        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) {
            throw new Error('User not authenticated')
        }

        // Fetch the library image
        const response = await fetch(libraryImageUrl)
        if (!response.ok) {
            throw new Error('Failed to fetch library image')
        }

        const blob = await response.blob()
        const arrayBuffer = await blob.arrayBuffer()

        // Generate filename
        const timestamp = Date.now()
        const fileName = `avatar-${timestamp}.jpg`

        console.log('Uploading library image to storage:', fileName)

        // Upload to user's storage
        const { data, error } = await supabase.storage
            .from('agent-avatars')
            .upload(fileName, arrayBuffer, {
                cacheControl: '3600',
                upsert: true,
                contentType: blob.type || 'image/jpeg'
            })

        if (error) {
            console.error('Upload error:', error)
            throw error
        }

        const { data: { publicUrl } } = supabase.storage
            .from('agent-avatars')
            .getPublicUrl(data.path)

        console.log('Library image copied successfully:', publicUrl)

        return publicUrl
    },

    /**
     * Create a new agent
     */
    async createAgent(userId: string, agentData: {
        name: string
        handle: string
        description: string
        function: string
        avatarFile?: File
    }): Promise<Agent> {
        try {
            // Upload avatar if provided
            let avatarUrl: string | null = null
            if (agentData.avatarFile) {
                console.log('Uploading avatar...', agentData.avatarFile)
                try {
                    avatarUrl = await this.uploadAvatar(userId, agentData.avatarFile)
                    console.log('Avatar uploaded successfully:', avatarUrl)
                } catch (uploadError) {
                    console.error('Avatar upload failed:', uploadError)
                    // Continue without avatar if upload fails
                    avatarUrl = null
                }
            }

            // Create agent record
            console.log('Inserting agent into database...')
            const { data, error } = await supabase
                .from('agents')
                .insert({
                    user_id: userId,
                    name: agentData.name,
                    handle: agentData.handle,
                    description: agentData.description,
                    function_description: agentData.function,
                    avatar_url: avatarUrl,
                    status: 'active', // All agents are active by default (verified badge)
                    version: '1.0.0'
                })
                .select()
                .single()

            if (error) {
                console.error('Database insert error:', error)
                throw error
            }

            console.log('Agent created successfully:', data)

            // ✨ Initialize files for the agent (using expanded structure)
            console.log('Initializing expanded file structure for agent...')
            try {
                await agentTemplateService.initializeAgentFiles(data.id, data.name, true) // true = use expanded structure
                console.log('Expanded file structure initialized successfully (100+ files)')

                // 🔍 Index files into RAG for knowledge retrieval
                console.log('Indexing agent files into RAG...')
                try {
                    const { data: { session } } = await supabase.auth.getSession()
                    if (session?.access_token) {
                        const indexResponse = await fetch(`${import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000'}/api/v1/agents/${data.id}/index`, {
                            method: 'POST',
                            headers: {
                                'Authorization': `Bearer ${session.access_token}`
                            }
                        })

                        if (indexResponse.ok) {
                            const indexResult = await indexResponse.json()
                            console.log(`✅ RAG Indexing complete: ${indexResult.chunks_indexed} chunks from ${indexResult.files_processed} files`)
                        } else {
                            console.warn('⚠️ RAG indexing failed:', await indexResponse.text())
                        }
                    }
                } catch (indexError) {
                    console.error('RAG indexing error:', indexError)
                    // Don't fail agent creation if indexing fails
                }
            } catch (fileError) {
                console.error('Error initializing files:', fileError)
                // Don't fail agent creation if file initialization fails
            }

            return data as unknown as Agent
        } catch (error) {
            console.error('createAgent error:', error)
            throw error
        }
    },

    /**
     * Update agent profile information
     */
    async updateAgentProfile(agentId: string, updates: {
        name?: string
        description?: string | null
        function_description?: string | null
        avatar_url?: string | null
        banner_url?: string | null
    }): Promise<Agent> {
        const { data, error } = await supabase
            .from('agents')
            .update(updates)
            .eq('id', agentId)
            .select()
            .single()

        if (error) {
            console.error('Error updating agent profile:', error)
            throw error
        }

        return data as unknown as Agent
    },

    /**
     * Get agent by handle
     */
    async getAgentByHandle(handle: string): Promise<Agent | null> {
        const { data, error } = await supabase
            .from('agents')
            .select('*')
            .eq('handle', handle)
            .single()

        if (error || !data) {
            console.error('Error fetching agent by handle:', error)
            return null
        }

        return data as unknown as Agent
    }
}
